// Session PJ2: add a lift to the project's survey list without booking a visit. A lift is either one already on the project's lift list
// or a name typed here (a lift not yet on the register, or a block with no number). Nothing is created on the register: the survey
// only names the lift. Booking a visit then puts an engineer on it.
import { useState } from "react";
import { api, type ProjectSurveysView } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN, ModalShell } from "../../../components/ModalShell";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function AddLiftModal({ view, onClose, onAdded }: { view: ProjectSurveysView; onClose: () => void; onAdded: () => void }) {
  const have = new Set(view.surveys.filter((s) => s.supersededById == null).map((s) => s.assetId).filter((x): x is string => !!x));
  const available = view.project.lifts.filter((l) => !have.has(l.assetId));
  const [assetId, setAssetId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blocker = !assetId && !label.trim() ? "Choose a lift, or type its name." : null;

  async function add() {
    if (blocker) return;
    setBusy(true);
    setError(null);
    try {
      await api.projectSurveysOffice.addLift(view.project.id, assetId ? { assetId, preview: false } : { label: label.trim(), preview: false });
      onAdded();
    } catch (e) {
      setError(friendlyApiError(e, "Could not add that lift."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Add a lift" onClose={() => !busy && onClose()} width="max-w-md" panelClassName="p-5">
      <h2 className="font-heading text-xl text-ele-strong">Add a lift</h2>
      <p className="mt-1 text-sm text-ele-body">This adds a survey for the lift. It does not add anything to the lift register.</p>
      {available.length > 0 ? (
        <div className="mt-4">
          <label className={labelCls} htmlFor="add-lift-asset">A lift on this project</label>
          <select id="add-lift-asset" className={inputCls} value={assetId} onChange={(e) => setAssetId(e.target.value)}>
            <option value="">Choose...</option>
            {available.map((l) => (
              <option key={l.assetId} value={l.assetId}>{l.label}</option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="mt-4">
        <label className={labelCls} htmlFor="add-lift-label">{available.length > 0 ? "Or a name for another lift" : "Name of the lift"}</label>
        <input id="add-lift-label" className={inputCls} value={label} maxLength={120} disabled={!!assetId} onChange={(e) => setLabel(e.target.value)} placeholder="For example Lift 3, or Block B east" />
      </div>
      {error ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p> : null}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void add()} disabled={busy || blocker !== null}>
          {busy ? "Adding..." : "Add the lift"}
        </button>
      </div>
    </ModalShell>
  );
}
